// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type ShareCardStanding = { id: string; name: string; value: string };
export type ShareCardLine = { id: string; label: string; value: string };
export type ShareCardSection = { id: string; title: string; lines: ShareCardLine[] };

export type ShareCardContent = {
  title: string;
  subtitle?: string;
  /** Heading over the standings value column, e.g. "Merits". */
  valueLabel: string;
  standings: ShareCardStanding[];
  /** Condensed blocks under the standings: one line per game, per highlight. */
  sections: ShareCardSection[];
  footer?: string;
};

// Card geometry in SVG user units, which are also the exported pixels.
export const CARD_WIDTH = 1080;
export const PADDING = 56;
export const ROW_HEIGHT = 72;
export const ROW_GAP = 8;
export const SECTION_TITLE_HEIGHT = 54;
export const LINE_HEIGHT = 40;
export const MORE_HEIGHT = 38;
export const FOOTER_HEIGHT = 64;
export const SECTION_GAP = 18;

/**
 * Height ceiling for the export, and the reason this file exists.
 *
 * WhatsApp and friends re-encode a shared photo down to roughly 1600px on its longest edge. A
 * 1080px-wide picture therefore survives intact only while it stays under about twice that width
 * — past that the downscale eats the width instead, and the text turns to mush. So the card is
 * budgeted to fit rather than allowed to grow: standings first, then as many condensed lines as
 * the remainder affords, with the overflow reported as "+N more".
 */
export const MAX_CARD_HEIGHT = 1920;

/** Standings are the point of the card, so they get most of the budget before sections do. */
const STANDINGS_BUDGET_SHARE = 0.62;
const MIN_STANDINGS_ROWS = 3;

export type ShareCardLayout = {
  height: number;
  standings: ShareCardStanding[];
  standingsMore: number;
  sections: Array<{ id: string; title: string; lines: ShareCardLine[]; more: number }>;
  sectionsMore: number;
};

const headerHeight = (content: ShareCardContent) =>
  PADDING + 44 + 62 + (content.subtitle ? 44 : 0) + 58;

/** Trims the content to what fits inside MAX_CARD_HEIGHT and reports what was left out. */
export const layoutShareCard = (
  content: ShareCardContent,
  maxHeight: number = MAX_CARD_HEIGHT
): ShareCardLayout => {
  const chrome = headerHeight(content) + (content.footer ? FOOTER_HEIGHT : 0) + PADDING;
  const budget = Math.max(0, maxHeight - chrome);

  // --- standings ---------------------------------------------------------
  const rowSpan = ROW_HEIGHT + ROW_GAP;
  const wanted = content.standings.length;
  const affordable = Math.max(MIN_STANDINGS_ROWS, Math.floor((budget * STANDINGS_BUDGET_SHARE) / rowSpan));
  let taken = Math.min(wanted, affordable);
  let standingsHeight = taken * rowSpan;
  if (taken < wanted) {
    // The "+N more" line has to come out of the same budget. The podium is never traded away for
    // it, though: a card that shows nobody is not worth sharing.
    const withMore = Math.floor((budget * STANDINGS_BUDGET_SHARE - MORE_HEIGHT) / rowSpan);
    taken = Math.min(wanted, Math.max(MIN_STANDINGS_ROWS, Math.min(taken, withMore)));
    standingsHeight = taken * rowSpan + (taken < wanted ? MORE_HEIGHT : 0);
  }
  const standings = content.standings.slice(0, taken);
  const standingsMore = wanted - taken;

  // --- sections ----------------------------------------------------------
  let remaining = budget - standingsHeight;
  const sections: ShareCardLayout['sections'] = [];
  let sectionsMore = 0;

  content.sections
    .filter((section) => section.lines.length > 0)
    .forEach((section) => {
      const minimum = SECTION_GAP + SECTION_TITLE_HEIGHT + LINE_HEIGHT;
      if (remaining < minimum) {
        sectionsMore += 1;
        return;
      }
      let available = remaining - SECTION_GAP - SECTION_TITLE_HEIGHT;
      let lines = Math.min(section.lines.length, Math.floor(available / LINE_HEIGHT));
      let more = section.lines.length - lines;
      if (more > 0) {
        available -= MORE_HEIGHT;
        lines = Math.max(1, Math.min(lines, Math.floor(available / LINE_HEIGHT)));
        more = section.lines.length - lines;
      }
      sections.push({ id: section.id, title: section.title, lines: section.lines.slice(0, lines), more });
      remaining -= SECTION_GAP + SECTION_TITLE_HEIGHT + lines * LINE_HEIGHT + (more > 0 ? MORE_HEIGHT : 0);
    });

  const used =
    standingsHeight +
    sections.reduce(
      (total, section) =>
        total + SECTION_GAP + SECTION_TITLE_HEIGHT + section.lines.length * LINE_HEIGHT + (section.more > 0 ? MORE_HEIGHT : 0),
      0
    );

  return { height: chrome + used, standings, standingsMore, sections, sectionsMore };
};
